import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, LayoutGrid, Plus, Search, Trash2, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import {
  useBrandContextStore,
  useSelectedBrandId,
} from '@/lib/branding/brandContextStore';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth/useAuth';
import { usePlanQuota } from '@/lib/permissions/usePlanQuota';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { NewBrandDialog } from '@/features/brands/NewBrandDialog';
import { DeleteBrandDialog } from '@/features/brands/DeleteBrandDialog';
import { HelpHint } from '@/features/help/HelpHint';
import type { XkBrand } from '@/lib/api/types';

/**
 * Topbar pill — anchors workspace context to the LEFT of the topbar.
 *
 * Render: [companyLogo] CompanyName › BrandName ▼
 *
 * Click opens a Radix popover with a search input (with autoclear) and the
 * list of brands the user has access to (filtered backend-side by
 * `GET /xk-company`). Selecting a brand persists `selectedBrandId` and
 * navigates to `/brand/:id`.
 *
 * The popover exists whenever there is something to do in it: more than one
 * brand to choose from, or permission to create one. Only a user who can do
 * neither (a pending member with a single brand) gets the static pill.
 */
export function WorkspaceSwitcher() {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { company } = useCurrentCompany();
  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });

  const selectedBrandId = useSelectedBrandId();
  const setSelectedBrandId = useBrandContextStore((s) => s.setSelectedBrandId);

  // "New brand" entry point. Visible to anyone past onboarding (pending
  // staff/viewer roles can't create). The precise permission + plan cap are
  // enforced server-side (@Roles + @PlanGated on POST /co-companies); the
  // plan cap is also pre-checked on click to pivot to the upgrade prompt.
  const { isPending } = useAuth();
  const canCreateBrand = !isPending;
  const brandQuota = usePlanQuota('maxBrands');
  const [newBrandOpen, setNewBrandOpen] = useState(false);
  /** La marca que se está a punto de borrar. `null` = ningún diálogo abierto. */
  const [borrando, setBorrando] = useState<XkBrand | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const brands = brandsQ.data?.items ?? [];
  const selectedBrand =
    brands.find((b) => b.id === selectedBrandId) ?? brands[0] ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.name?.toLowerCase().includes(q));
  }, [brands, query]);

  if (!company) return null;

  // El desplegable existe cuando hay más de una marca entre las que elegir O
  // cuando el usuario puede crear una. Lo segundo no se condiciona al tipo de
  // cuenta: una cuenta recién creada tiene exactamente una marca (su propia
  // empresa) y, sin este desplegable, no hay ningún otro sitio desde el que dar
  // de alta la siguiente (bloqueo reportado el 26 sep 2026). Una cuenta de
  // marca con una sola marca abre directamente en ella igualmente: el
  // desplegable sólo aparece si lo pide.
  const switchable = brands.length > 1 || canCreateBrand;

  const openCreate = () => {
    setOpen(false);
    if (brandQuota.reached) setUpgradeOpen(true);
    else setNewBrandOpen(true);
  };
  const handleCreated = (brand: XkBrand) => {
    setSelectedBrandId(brand.id);
    setNewBrandOpen(false);
    navigate(`/brand-studio/${brand.id}`);
  };

  const triggerInner = (
    <span className="flex min-w-0 items-center gap-2">
      {company.logo ? (
        <img
          src={resolveImageUrl(company.logo)}
          alt={company.name ?? ''}
          className="h-4 max-w-[80px] flex-none object-contain"
        />
      ) : null}
      <span
        className="truncate text-xs font-bold text-[var(--color-xk-lavender)]"
        title={company.name ?? ''}
      >
        {company.name}
      </span>
      <span className="text-[var(--color-xk-text-muted)]">›</span>
      <span
        className="truncate text-xs font-bold text-[var(--color-accent-ink)]"
        title={selectedBrand?.name ?? ''}
      >
        {selectedBrand?.name ?? t('topbar.workspaceSwitcher.noBrand')}
      </span>
      {switchable && (
        <ChevronDown className="h-3.5 w-3.5 flex-none text-[var(--color-xk-text-muted)]" />
      )}
    </span>
  );

  const triggerClass = cn(
    'hidden max-w-[420px] items-center gap-2 rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-1.5 transition-colors lg:flex',
    switchable && 'hover:border-[var(--color-xk-border-strong)]',
  );

  if (!switchable) {
    return (
      <div className="flex items-center gap-1.5">
        <div className={triggerClass}>{triggerInner}</div>
        <HelpHint
        section="workspace-switcher"
        className="hidden h-8 w-8 shrink-0 lg:inline-grid"
      />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Popover.Root
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (v) {
            // Focus the search input as soon as the popover mounts.
            setTimeout(() => inputRef.current?.focus(), 0);
          } else {
            setQuery('');
          }
        }}
      >
        <Popover.Trigger asChild>
          <button type="button" className={triggerClass}>
            {triggerInner}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            className="z-50 w-[320px] rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-2 shadow-2xl"
          >
            {/* Search input */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-xk-text-muted)]" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('topbar.workspaceSwitcher.searchPlaceholder')}
                aria-label={t('topbar.workspaceSwitcher.searchPlaceholder')}
                className="w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] py-1.5 pl-8 pr-7 text-xs text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                  aria-label={t('topbar.workspaceSwitcher.clear')}
                  title={t('topbar.workspaceSwitcher.clear')}
                  className="absolute right-1 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)]"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            {/* Results */}
            <ul className="mt-2 max-h-[320px] overflow-y-auto scrollbar-thin">
              {filtered.length === 0 ? (
                <li className="px-3 py-3 text-center text-[11px] text-[var(--color-xk-text-muted)]">
                  {t('topbar.workspaceSwitcher.empty')}
                </li>
              ) : (
                filtered.map((b) => {
                  const isActive = b.id === selectedBrand?.id;
                  return (
                    /*
                      `<li>` con dos botones hermanos, no un botón dentro de
                      otro: anidarlos es HTML inválido y el navegador reordena
                      el árbol por su cuenta, con lo que el clic de borrar
                      acababa cambiando de marca.
                    */
                    <li key={b.id} className="group/marca relative flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedBrandId(b.id);
                          setOpen(false);
                          navigate(`/brand/${b.id}`);
                        }}
                        className={cn(
                          'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors',
                          isActive
                            ? 'bg-[var(--color-accent-08)] text-[var(--color-accent-ink)]'
                            : 'text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]',
                        )}
                      >
                        {b.logo ? (
                          <img
                            src={resolveImageUrl(b.logo)}
                            alt=""
                            className="h-5 w-5 flex-none rounded object-cover"
                          />
                        ) : (
                          <span
                            className="grid h-5 w-5 flex-none place-items-center rounded bg-[var(--color-xk-surface)] text-[10px] font-extrabold text-[var(--color-xk-lavender)]"
                            aria-hidden
                          >
                            {(b.name ?? '?').charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate font-semibold">
                          {b.name}
                        </span>
                        {isActive && (
                          <Check className="h-3.5 w-3.5 flex-none text-[var(--color-accent-ink)]" />
                        )}
                      </button>

                      {/*
                        Borrar aparece al pasar por encima, no siempre.

                        Una papelera fija junto a cada marca convierte una lista
                        que se usa veinte veces al día para CAMBIAR de marca en
                        un campo de minas. Al pasar por encima sigue estando a un
                        gesto para quien la busca, y no está en el camino de
                        quien sólo quiere cambiar.
                      */}
                      {canCreateBrand && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setBorrando(b);
                          }}
                          aria-label={t('brandDelete.action', { name: b.name ?? '' })}
                          title={t('brandDelete.action', { name: b.name ?? '' })}
                          className="ml-1 flex-none rounded-md p-1.5 text-[var(--color-xk-text-muted)] opacity-0 transition-opacity hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-error)] focus-visible:opacity-100 group-hover/marca:opacity-100"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })
              )}
            </ul>

            {/* Pie: «Nuevo brand» (por permiso; el cupo del plan se mira al
                pulsar → UpgradeDialog) y «Todas las marcas», la galería con
                la identidad y los números de cada una. */}
            <div className="mt-2 flex items-center gap-1 border-t border-[var(--color-xk-border)] pt-2">
              {canCreateBrand && (
                <button
                  type="button"
                  onClick={openCreate}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-bold text-[var(--color-accent-ink)] transition-colors hover:bg-[var(--color-xk-surface)]"
                >
                  <span className="grid h-5 w-5 flex-none place-items-center rounded bg-[var(--color-accent-08)]">
                    <Plus className="h-3.5 w-3.5" />
                  </span>
                  <span className="truncate">{t('brand.new.cta')}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/brands');
                }}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-bold text-[var(--color-xk-lavender)] transition-colors hover:bg-[var(--color-xk-surface)]"
              >
                <span className="grid h-5 w-5 flex-none place-items-center rounded bg-[var(--color-xk-surface)]">
                  <LayoutGrid className="h-3.5 w-3.5" />
                </span>
                <span className="truncate">{t('brand.list.cta')}</span>
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <HelpHint
        section="workspace-switcher"
        className="hidden h-8 w-8 shrink-0 lg:inline-grid"
      />
      {borrando && (
        <DeleteBrandDialog
          brand={borrando}
          open
          onOpenChange={(v) => !v && setBorrando(null)}
          onDeleted={(b) => {
            setBorrando(null);
            setOpen(false);
            /*
              Si se borró la marca en la que se estaba trabajando hay que
              soltarla: dejar el id seleccionado apuntando a algo que ya no
              existe deja media aplicación pidiendo datos de una marca borrada.
              Se pasa a la primera que quede, y si no queda ninguna, a la raíz,
              que ya sabe pedir crear una.
            */
            if (b.id !== selectedBrandId) return;
            const queda = brands.find((x) => x.id !== b.id) ?? null;
            setSelectedBrandId(queda?.id ?? null);
            navigate(queda ? `/brand/${queda.id}` : '/');
          }}
        />
      )}
      <NewBrandDialog
        open={newBrandOpen}
        onOpenChange={setNewBrandOpen}
        onCreated={handleCreated}
        onQuotaExceeded={() => setUpgradeOpen(true)}
      />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="maxBrands"
        currentPlanName={brandQuota.planName}
        limit={brandQuota.limit}
      />
    </div>
  );
}
